import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Route, Routes, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom'
import { Trophy as TrophyIcon, Users, Shuffle, Award, ListOrdered, Target, Lock as LockIcon, MapPin as MapPinIcon, PlusCircle, Flag, CloudSun, Calendar, Image as ImageIcon, Tv as TvIcon, DollarSign, MessageSquare } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { get, onValue, ref as dbRef } from 'firebase/database'
import { db } from '../lib/firebase.js'

import PinGate, { clearAdminSession, confirmAccessCodeIfLocked } from './PinGate.jsx'
import HelpWizard from './HelpWizard.jsx'
import { HANDICAP_MODE_OPTIONS, ALLOWANCE_OPTIONS, DEFAULT_ALLOWANCE } from '../lib/handicap.js'
import CourseSearchPicker from '../components/CourseSearchPicker.jsx'
import {
  bulkAddRegistrations,
  createTournament,
  deleteTournament,
  saveRegistrationList,
  watchTournament,
  watchTournaments,
  updateTournamentInfo,
  promoteRegistrationToTournament,
  watchClubCourses,
} from '../lib/data.js'
import { createQualifyingEventFromRegistration, watchQualifyingEvents } from '../lib/qualifyingEvents.js'
import { installQualifyingVanishMonitor } from '../lib/qeAudit.js'
import { demoRegistrationRows } from '../lib/demos.js'
import TournamentDetail from './TournamentDetail.jsx'
import RegistrationTab from './RegistrationTab.jsx'
import ClubData from './ClubData.jsx'
import DemosPractice from './DemosPractice.jsx'
import WelcomePage from './WelcomePage.jsx'
import ProShopCredit from './ProShopCredit.jsx'
import SpecialOrders from './SpecialOrders.jsx'
import GGPage from './gg/GGPage.jsx'
import GMembers from './gg/GMembers.jsx'
import GMemberProfile from './gg/GMemberProfile.jsx'
import GRepairs from './gg/GRepairs.jsx'
import GDemoClubs from './gg/GDemoClubs.jsx'
import GRentals from './gg/GRentals.jsx'
import GInventory from './gg/GInventory.jsx'
import GPurchaseOrders from './gg/GPurchaseOrders.jsx'
import GVendors from './gg/GVendors.jsx'
import GPOS from './gg/GPOS.jsx'
import GTasks from './gg/GTasks.jsx'
import GLostFound from './gg/GLostFound.jsx'
import GDocuments from './gg/GDocuments.jsx'
import GLessons from './gg/GLessons.jsx'
import GEmployees from './gg/GEmployees.jsx'
import GProReports from './gg/GProReports.jsx'
import GSettings from './gg/GSettings.jsx'
import GShell from './gg/GShell.jsx'
import GFeaturesHub from './gg/GFeaturesHub.jsx'
import GFormats from './gg/GFormats.jsx'
import GMultiFormat from './gg/GMultiFormat.jsx'
// Restored games suite (from the 2026-07-08 backup) — the full playable-game set.
import GGamesPlay from './gg/GGamesPlay.jsx'
import GWolf from './gg/GWolf.jsx'
import GVegas from './gg/GVegas.jsx'
import GBingoBangoBongo from './gg/GBingoBangoBongo.jsx'
import GSnake from './gg/GSnake.jsx'
import GDots from './gg/GDots.jsx'
import GChicago from './gg/GChicago.jsx'
import GDogfight from './gg/GDogfight.jsx'
import GNines from './gg/GNines.jsx'
import GRabbit from './gg/GRabbit.jsx'
import GScotch from './gg/GScotch.jsx'
import GShamble from './gg/GShamble.jsx'
import GShuffle from './gg/GShuffle.jsx'
import GSixes from './gg/GSixes.jsx'
import GMatchPlay from './gg/GMatchPlay.jsx'
import GStrokePlay from './gg/GStrokePlay.jsx'
import GTeamCup from './gg/GTeamCup.jsx'
import GFourBall from './gg/GFourBall.jsx'
import GPractice from './gg/GPractice.jsx'
import GHandicap from './gg/GHandicap.jsx'
const GAME_PAGES = [
  { path: 'wolf', label: 'Wolf', C: GWolf },
  { path: 'vegas', label: 'Vegas', C: GVegas },
  { path: 'bingo', label: 'Bingo Bango Bongo', C: GBingoBangoBongo },
  { path: 'snake', label: 'Snake', C: GSnake },
  { path: 'dots', label: 'Dots', C: GDots },
  { path: 'chicago', label: 'Chicago', C: GChicago },
  { path: 'dogfight', label: 'Dogfight', C: GDogfight },
  { path: 'nines', label: 'Nines', C: GNines },
  { path: 'rabbit', label: 'Rabbit', C: GRabbit },
  { path: 'scotch', label: 'Scotch', C: GScotch },
  { path: 'shamble', label: 'Shamble', C: GShamble },
  { path: 'shuffle', label: 'Shuffle', C: GShuffle },
  { path: 'sixes', label: 'Sixes', C: GSixes },
  { path: 'match-play', label: 'Match Play', C: GMatchPlay },
  { path: 'stroke-play', label: 'Stroke Play', C: GStrokePlay },
  { path: 'team-cup', label: 'Team Cup', C: GTeamCup },
  { path: 'four-ball', label: 'Four-Ball', C: GFourBall },
  { path: 'practice', label: 'Practice', C: GPractice },
  { path: 'handicap', label: 'Handicap', C: GHandicap },
]
export { GAME_PAGES }
import {
  QualifyingEventsSection,
  QualifyingEventDetailPage,
  TournamentCreatedRosterModal,
} from './QualifyingEvents.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import { trialSignOut } from '../auth/TrialGate.jsx'
import {
  PbccAdminSidebar,
  PbccHeroHeader,
  PbccPrimaryButton,
  PbccSectionTitle,
  fourBallNavItems,
  BackToHome,
} from '../components/PbccLayout.jsx'
import CLUB from '../config/club.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'

// Premium (test-only) inline helpers — used where CSS can't reach inline styles.
// Club/live (PBCC_PREMIUM false) keep their original white/green values.
const PREM_CARD = PBCC_PREMIUM
  ? { backgroundColor: '#F7F2E6', border: '1.5px solid #C2A24E', boxShadow: '0 10px 28px rgba(0,0,0,0.28)' }
  : null
const PREM_PILL_BTN = PBCC_PREMIUM
  ? { color: '#F7F2E6', background: '#123D26', border: '1px solid #C2A24E' }
  : null
const PREM_PANEL = PBCC_PREMIUM
  ? { background: '#F7F2E6', border: '1px solid #C2A24E' }
  : null
import { useDbValue } from '../lib/useDbValue.js'
import CalcuttaStandalone from './CalcuttaStandalone.jsx'
import PlayerMessageStandalone from './PlayerMessageStandalone.jsx'
import DataGate from '../components/DataGate.jsx'
import EventTitle from '../components/EventTitle.jsx'
import { buildTournamentFromQualifying } from '../lib/buildTournament.js'
import { AdminNavProvider, AdminShell, useSetAdminNav } from './AdminShell.jsx'

export default function AdminApp() {
  return (
    <PinGate>
      <AdminNavProvider>
        <AdminShell>
      <Routes>
        {/* 4-Ball site has NO Golf Ops / Pro Shop dashboard — the admin home IS
            the 4-Ball page. /admin redirects there so WelcomePage never renders
            (it's what "Golf Ops / Pro Shop Operations" was). */}
        <Route index element={FOURBALL_ONLY ? <Navigate to="/admin/pbcc-4ball" replace /> : <WelcomePage />} />
        <Route path="pbcc-4ball" element={<AdminHome />} />
        <Route path="club-data" element={<ClubData />} />
        <Route path="calcutta" element={<CalcuttaStandalone />} />
        <Route path="player-message" element={<PlayerMessageStandalone />} />
        <Route path="t/:tournamentId/*" element={<TournamentRoute />} />
        <Route path="qualifying/:eventId" element={<QualifyingEventDetailPage />} />
        {/* 4-Ball-only test site removes Weather, Demos & Practice, and the entire
            Pro Shop / retail suite (pro-shop-credit, special-orders, gg/*). */}
        {!FOURBALL_ONLY && <>
          <Route path="weather" element={<WelcomePage weatherOnly />} />
          <Route path="demos" element={<DemosPractice />} />
          <Route path="pro-shop-credit" element={<ProShopCredit />} />
          <Route path="special-orders" element={<SpecialOrders />} />
          <Route path="gg" element={<GGPage />} />
          <Route path="gg/members" element={<GMembers />} />
          <Route path="gg/members/:memberId" element={<GMemberProfile />} />
          <Route path="gg/repairs" element={<GRepairs />} />
          <Route path="gg/demo-clubs" element={<GDemoClubs />} />
          <Route path="gg/rentals" element={<GRentals />} />
          <Route path="gg/inventory" element={<GInventory />} />
          <Route path="gg/purchase-orders" element={<GPurchaseOrders />} />
          <Route path="gg/vendors" element={<GVendors />} />
          <Route path="gg/pos" element={<GPOS />} />
          <Route path="gg/tasks" element={<GTasks />} />
          <Route path="gg/lost-found" element={<GLostFound />} />
          <Route path="gg/documents" element={<GDocuments />} />
          <Route path="gg/lessons" element={<GLessons />} />
          <Route path="gg/employees" element={<GEmployees />} />
          <Route path="gg/reports" element={<GProReports />} />
          <Route path="gg/settings" element={<GSettings />} />
          {/* G-Features: the games engine + handicap system hub, and the game
              format pages (wrapped in GShell → same chrome + open gate). */}
          <Route path="gg-features" element={<GFeaturesHub />} />
          <Route path="gg/formats" element={<GShell active="gfeatures"><GFormats /></GShell>} />
          <Route path="gg/multi-format" element={<GShell active="gfeatures"><GMultiFormat /></GShell>} />
          {/* Restored games — play harness + every standalone game, each in GShell (open gate). */}
          <Route path="gg/games" element={<GShell active="gfeatures"><GGamesPlay /></GShell>} />
          {GAME_PAGES.map(({ path, C }) => (
            <Route key={path} path={`gg/games/${path}`} element={<GShell active="gfeatures"><C /></GShell>} />
          ))}
        </>}
        {/* Any removed/unknown admin URL → the 4-Ball home (NOT /admin, which is
            the Golf Ops dashboard). This is what was landing users on Golf Ops. */}
        {FOURBALL_ONLY && <Route path="*" element={<Navigate to="/admin/pbcc-4ball" replace />} />}
      </Routes>
        </AdminShell>
      </AdminNavProvider>
    </PinGate>
  )
}

function TournamentRoute() {
  const { tournamentId } = useParams()
  const { data: t, status, error, retry } = useDbValue(tournamentId ? `tournaments/${tournamentId}` : null)
  if (status === 'loading' || status === 'error') {
    return <div className="p-10"><DataGate status={status} error={error} retry={retry} label="Loading tournament…" /></div>
  }
  if (!t) return <div className="p-10 text-center text-gray-500">Tournament not found.</div>
  return (
    <>
      <TournamentDetail tournamentId={tournamentId} tournament={t} />
      {/* Floating AI Assistant — appears on every admin page inside a live
          tournament. Hidden on the dashboard (no tournamentId). */}
      <AIAssistant tournamentId={tournamentId} />
    </>
  )
}

function AdminHome() {
  const navigate = useNavigate()
  const location = useLocation()
  const tRes = useDbValue('tournaments')
  const tournaments = tRes.data || {}
  // Two-step create flow:
  //   showTypePicker  → grid of format tiles; clicking one locks the
  //                     format and opens CreateModal pre-filled.
  //   creatingPreset  → preset key chosen on the picker; when non-null
  //                     CreateModal renders with that format locked in.
  const [showBuildPicker, setShowBuildPicker] = useState(false)
  const [showTypePicker, setShowTypePicker] = useState(false)
  const [creatingPreset, setCreatingPreset] = useState(null)
  const [showHelp, setShowHelp] = useState(false)
  // String 'fullTournament' | 'strokePlay' | null. Set as soon as the
  // admin launches one of the demo buttons that needs an AI lock-in
  // (Full Tournament Demo → COPY from /templates/demos/pbcc-4-ball,
  // Stroke Play Demo → COPY from /templates/demos/stroke-play-36).
  const [demoMode, setDemoMode] = useState(null)
  // Roster size (96/104) chosen on the demo launcher toggle — handed to the
  // floating AI so its demo build clones the size-correct good template.
  const [demoSize, setDemoSize] = useState(96)
  // Qualifying events drive the PBCC 4-Ball rolling section order (see currentStage).
  const qRes = useDbValue('qualifyingEvents')
  const qualifyingEvents = qRes.data || {}
  // TEST-ONLY tripwire: record (with stack + timestamp, into /qe_audit) whenever
  // a qualifying event disappears or is emptied, so the exact caller can be read
  // back via REST. No-ops on the club DB. Remove once the vanish bug is found.
  useEffect(() => installQualifyingVanishMonitor(), [])
  // Single entry point for "I want to create a tournament" — always
  // routes through the type picker so directors can't skip the format
  // decision. The picker calls onPick which advances to CreateModal.
  const openCreateFlow = () => {
    // Phase 5: New Tournament leads with the "build a ready event" picker
    // (promotes a registration / approved qualifying field), with "start fresh"
    // as the fallback into the format picker.
    setCreatingPreset(null)
    setShowTypePicker(false)
    setShowBuildPicker(true)
  }

  // (tournaments + qualifyingEvents now load via useDbValue above — robust
  // status/error/retry instead of fire-and-forget onValue.)

  // Split into three lists so the dashboard can show a clean separation:
  //   realList   — real tournaments admin owns (live data, real scoring)
  //   demoList   — sandbox demos / practice tournaments (isDemo:true)
  //   closedList — finished real events; demos never get "closed"
  // Guard against Firebase ever handing back a partial/non-object value
  // for an entry — this happened during back-navigation when the snapshot
  // arrived mid-write and crashed AdminHome into the ErrorBoundary.
  // Test Station tournaments are tagged isTestStation in addition to isDemo;
  // they live on the /teststation page only and must not pollute the
  // dashboard lists (My Tournaments / Demos / Closed).
  const allLive = Object.entries(tournaments)
    .filter(([, v]) => v && typeof v === 'object')
    .map(([id, v]) => ({ id, ...(v?.info || {}), results: v?.results }))
    .filter((t) => !t.isTestStation)
  const realList = allLive
    // Show real events AND practice builds (badged) — a practice field that ran
    // the whole flow lands here flagged, so the flow is visibly verifiable.
    .filter((t) => t.status !== 'closed' && (!t.isDemo || t.isPractice) && (t.stage || 'registration') === 'active')
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  const demoList = allLive
    .filter((t) => !!t.isDemo)
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  const closedList = allLive
    .filter((t) => t.status === 'closed' && !t.isDemo)
    .sort((a, b) => (b.closedAt || b.createdAt || 0) - (a.closedAt || a.createdAt || 0))

  // Post-create roster modal — populated only when the admin picked a
  // qualifying event in the CreateModal's "Import From Qualifying
  // Event" dropdown. When null, the normal navigate-to-tournament
  // behavior runs.
  const [postCreateRoster, setPostCreateRoster] = useState(null) // { tournamentId, approvedTeams }

  const handleCreate = async (info, opts = {}) => {
    try {
      // Hand-off marker: when set, after createTournament we read the
      // selected qualifying event's approved teams and import them as
      // registrations. None → normal create + navigate flow.
      const qualifyingEventId = opts?.qualifyingEventId || null
      // Phase 1: tournaments created here (fresh or qualifying-import) are built
      // events → stage 'active' so they show under My Tournaments, not Registration.
      const id = await createTournament({ ...info, stage: info.stage || 'active' })
      setCreatingPreset(null)
      setShowTypePicker(false)
      if (qualifyingEventId) {
        // Read approved teams from /qualifyingEvents/{eid}/teams. status
        // === 'approved' OR approved === true both qualify so seeded
        // demo data (status:'approved') and live admin-approved data
        // (approved:true) both flow through.
        const snap = await get(dbRef(db, `qualifyingEvents/${qualifyingEventId}/teams`))
        const map = snap.val() || {}
        const approved = Object.values(map).filter((t) => {
          if (!t) return false
          if (t.status === 'approved') return true
          if (t.approved === true) return true
          return false
        })
        // Pull the event's display name once so the ai-prefill payload
        // gets human-readable context. Falls back to '' if missing —
        // the roster modal defaults to a neutral label.
        const infoSnap = await get(dbRef(db, `qualifyingEvents/${qualifyingEventId}/info`))
        const qualifyingEventName = infoSnap.val()?.name || ''
        console.log('[Create+Import] approved teams pulled from qualifying event:', approved.length, approved)
        const rows = approved.map((t) => {
          const teamName = String(t.teamName || '').trim() || 'Team'
          const players = [t.player1, t.player2]
            .filter((p) => p && String(p).trim().length > 0)
            .map((p) => String(p).trim())
          const qs = Number.isFinite(Number(t.qualifyingScore))
            ? Number(t.qualifyingScore)
            : (Number.isFinite(Number(t.score)) ? Number(t.score) : null)
          const row = { teamName, qualifyingStatus: 'complete' }
          if (players.length > 0) row.players = players
          if (qs != null) row.qualifyingScore = qs
          return row
        })
        console.log('[Create+Import] registration rows to write:', rows.length, rows.slice(0, 3))
        if (rows.length > 0) {
          const written = await bulkAddRegistrations(id, rows)
          console.log('[Create+Import] bulkAddRegistrations returned:', written)
          // Verification read — surface a real error if Firebase silently
          // dropped the write.
          const regSnap = await get(dbRef(db, `tournaments/${id}/registrations`))
          const regCount = Object.keys(regSnap.val() || {}).length
          console.log('[Create+Import] verification read /registrations count:', regCount)
          if (regCount === 0) {
            throw new Error(`Tournament ${id} created but verification found 0 registrations after writing ${rows.length} rows.`)
          }
        }
        // Hand off to the Step 2 roster modal — admin picks AI vs Manual
        // there. Navigation happens inside that modal's buttons. Names
        // flow through so the AI prefill message gets full context.
        setPostCreateRoster({
          tournamentId: id,
          approvedTeams: approved,
          tournamentName: info?.name || '',
          qualifyingEventName,
        })
        return
      }
      navigate(`/admin/t/${id}`)
    } catch (e) {
      console.error('[Create+Import] failed:', e)
      window.alert(e?.message || 'Could not create tournament.')
    }
  }

  // ── PBCC 4-Ball rolling order ─────────────────────────────────────────
  // Order the three sections by where the current event sits in the flow:
  // the active/next stage rides on top, finished stages roll down.
  //   registration  → Registration, Qualifying, New Tournament
  //   qualifying    → Qualifying, New Tournament, Registration
  //   newTournament → New Tournament, Qualifying, Registration
  // Drive the roll off the CURRENT event's stage. State 3 HOLDS whenever a
  // tournament has been BUILT (it's in My Tournaments now — the event being
  // worked stays on top) OR a qualifying field is approved & locked and ready
  // to build. A built qualifying event (builtTournamentId set) no longer counts
  // as in-progress qualifying, but the built TOURNAMENT keeps the tab on State
  // 3. Truly empty — no built tournament AND nothing in progress — → State 1
  // (Registration on top).
  const qeList = Object.values(qualifyingEvents || {}).filter((v) => v && typeof v === 'object')
  const activeQes = qeList.filter((qe) => !qe?.info?.builtTournamentId)
  const anyReadyToBuild = activeQes.some((qe) => {
    const locked = (qe?.info?.status || 'open') === 'closed'
    const approved = qe?.teams
      ? Object.values(qe.teams).filter((t) => t && (t.approved === true || t.status === 'approved')).length
      : 0
    return locked && approved > 0
  })
  const anyQualifyingInProgress = activeQes.some((qe) => (qe?.info?.status || 'open') !== 'closed')
  const currentStage = (realList.length > 0 || anyReadyToBuild)
    ? 'newTournament'
    : anyQualifyingInProgress
      ? 'qualifying'
      : 'registration'
  const ROLL_ORDER = {
    registration:  ['registration', 'qualifying', 'newTournament'],
    qualifying:    ['qualifying', 'newTournament', 'registration'],
    newTournament: ['newTournament', 'qualifying', 'registration'],
  }

  // Shared 4-Ball sidebar tabs (also used by the standalone Weather page so it
  // stays in 4-Ball context). The three create tabs navigate here with ?new=;
  // the effect below fires the matching create flow on this page.
  const fourBallNav = fourBallNavItems(navigate)
  const scrollTo = (key) => document.querySelector(`[data-roll-key="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  useEffect(() => {
    const n = new URLSearchParams(location.search).get('new')
    if (!n) return
    if (n === 'registration') { scrollTo('registration'); window.dispatchEvent(new CustomEvent('pbcc4ball:new-registration')) }
    else if (n === 'qualifying') { scrollTo('qualifying'); window.dispatchEvent(new CustomEvent('pbcc4ball:new-qualifying')) }
    else if (n === 'tournament') { scrollTo('newTournament'); openCreateFlow() }
    navigate('/admin/pbcc-4ball', { replace: true }) // clear the flag so it doesn't re-fire
  }, [location.search]) // eslint-disable-line react-hooks/exhaustive-deps

  const heroHeader = (
    <PbccHeroHeader
      variant="admin"
      showCommandCenter={(realList.length + demoList.length) > 0}
      onLock={() => {
        clearAdminSession()
        location.reload()
      }}
      onHelp={() => setShowHelp(true)}
      onSignOut={() => {
        clearAdminSession()
        trialSignOut()
      }}
    />
  )
  const adminSidebar = (
    <PbccAdminSidebar
      active=""
      items={fourBallNav}
      onCreate={() => openCreateFlow()}
      onHelp={() => setShowHelp(true)}
    />
  )

  // 4-Ball: publish the three create-action tabs as this page's sidebar (its home
  // IS the tournaments page). The BIG site instead uses AdminShell's standard
  // SECTION nav (so you can reach Golf Ops / Handicap / Pro Shop), with a
  // "New Tournament" button in the content — so it must NOT publish a nav here.
  useSetAdminNav(FOURBALL_ONLY ? {
    items: fourBallNav.map((it) => ({ key: it.key, label: it.label, onClick: it.action })),
    status: (
      <>
        <div className="pbcc-status-label text-sm font-bold uppercase tracking-widest mb-2" style={{ color: '#C2A24E' }}>System Status</div>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#F7F2E6' }}><span className="pbcc-status-dot" /> All Systems Operational</div>
      </>
    ),
  } : null, [])

  const contentMain = (
    <main className={PBCC_PREMIUM
      ? 'pbcc-admin-content min-w-0 p-4 sm:p-8 flex flex-col min-h-0'
      : 'max-w-6xl w-full mx-auto p-4 sm:p-5 flex flex-col flex-1 min-h-0'}>
            <BackToHome />
            {(tRes.status === 'error' || qRes.status === 'error') && (
              <div className="mb-4">
                <DataGate
                  status="error"
                  error={tRes.error || qRes.error}
                  retry={() => { tRes.retry(); qRes.retry() }}
                  dark
                />
              </div>
            )}
            <RollingSections>
              {ROLL_ORDER[currentStage].map((key) => (
                <div key={key} data-roll-key={key}>
                  {key === 'registration' ? (
                    <RegistrationSection />
                  ) : key === 'qualifying' ? (
                    <QualifyingEventsSection />
                  ) : (
            <section>
              <div className="mb-3 flex items-start justify-between gap-3">
                <PbccSectionTitle
                  title="My Tournaments"
                  subtitle="Real tournaments — live scoring and player data."
                />
                {PBCC_PREMIUM && !FOURBALL_ONLY ? (
                  <PbccPrimaryButton onClick={() => openCreateFlow()} className="shrink-0 whitespace-nowrap">
                    + New Tournament
                  </PbccPrimaryButton>
                ) : null}
              </div>

              {realList.length === 0 ? (
                <div className="pbcc-empty-card min-h-[220px]">
                  <div
                    className="hidden sm:flex w-52 md:w-64 lg:w-80 shrink-0 items-center justify-center relative"
                    style={{
                      backgroundImage: `url(${CLUB.heroImage || CLUB.gpsSatelliteImage || '/pbcccourse.png'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: 'contrast(1.08) saturate(1.12)',
                    }}>
                  </div>
                  <div className="px-5 py-6 sm:px-8 flex flex-col gap-1.5 justify-center">
                    <h3 className="text-lg font-extrabold text-gray-900">No tournaments yet.</h3>
                    <p className="text-sm text-gray-600 leading-snug max-w-md">
                      Use <b>New Tournament</b> when you're ready.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={PBCC_PREMIUM ? 'pbcc-card-grid' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
                  {realList.map((t) => <TournamentListCard key={t.id} t={t} />)}
                </div>
              )}

              {closedList.length > 0 ? <PastTournaments closed={closedList} /> : null}
            </section>
                  )}
                </div>
              ))}
            </RollingSections>
    </main>
  )

  return (
    <>
      {PBCC_PREMIUM ? (
        // Content only — the shared AdminShell provides the full-height sidebar
        // (section nav on the big site, create-action tabs on 4-Ball) + zoom frame.
        <>
          {heroHeader}
          <div className="pbcc-main-panel-4ball pbcc-admin-body flex-1 min-h-0">
            {contentMain}
            <AdminRightRail />
          </div>
        </>
      ) : (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#123D26' }}>
          {heroHeader}
          <div className="flex flex-1 min-h-0">
            {adminSidebar}
            <div className="pbcc-main-panel flex-1 flex flex-col">
              {contentMain}
            </div>
          </div>
        </div>
      )}

      {showBuildPicker ? (
        <BuildPickerModal
          onClose={() => setShowBuildPicker(false)}
          onStartFresh={() => { setShowBuildPicker(false); setShowTypePicker(true) }}
        />
      ) : null}
      {showTypePicker ? (
        <TypePickerModal
          onClose={() => setShowTypePicker(false)}
          onPick={(key) => {
            // Lock in the chosen format and advance to the standard
            // CreateModal — its dropdown is disabled when a lockedPreset
            // is passed in, so the director can't change formats mid-form.
            setCreatingPreset(key)
            setShowTypePicker(false)
          }}
        />
      ) : null}
      {creatingPreset ? (
        <CreateModal
          lockedPreset={creatingPreset}
          onClose={() => setCreatingPreset(null)}
          onSave={handleCreate}
        />
      ) : null}
      {showHelp ? (
        <HelpWizard
          contextKey="home"
          onClose={() => setShowHelp(false)}
          onAction={() => openCreateFlow()}
        />
      ) : null}
      {/* Step-2 roster modal — appears after handleCreate finishes a
          create + qualifying-import. SET UP buttons inside the modal
          handle the actual navigation; closing clears the state. */}
      {postCreateRoster ? (
        <TournamentCreatedRosterModal
          tournamentId={postCreateRoster.tournamentId}
          approvedTeams={postCreateRoster.approvedTeams}
          tournamentName={postCreateRoster.tournamentName}
          qualifyingEventName={postCreateRoster.qualifyingEventName}
          onClose={() => setPostCreateRoster(null)}
        />
      ) : null}
      {/* Floating AI Assistant on the dashboard — no tournamentId so
          actions like CREATE/COPY/SETUP_COMPLETE_TOURNAMENT can run from
          the home screen before the director has clicked into anything. */}
      <AIAssistant tournamentId={null} demoMode={demoMode} demoSize={demoSize} />
    </>
  )
}

// GPS Map Test launcher modal — shared QR for testers plus a chooser between
// the two watcher experiences: PBCC custom course map (on-property
// calibration) and a real Google Maps view (anywhere-in-the-world sanity
// check before going on-course). Broadcaster URL is the same for both.
export function GpsTestModal({ onClose }) {
  const [count, setCount] = useState(0)
  const [copied, setCopied] = useState(false)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const broadcasterUrl = `${origin}/gpstest`
  const pbccWatcherUrl = `${origin}/gpstestwatch`
  const liveWatcherUrl = `${origin}/gpslivetest`

  useEffect(() => {
    const off = onValue(dbRef(db, 'gpstest'), (snap) => {
      const v = snap.val() || {}
      const now = Date.now()
      const TEN_MIN = 10 * 60 * 1000
      let n = 0
      for (const t of Object.values(v)) {
        if (!t || !Number.isFinite(t.lat) || !Number.isFinite(t.lng)) continue
        if (now - (t.updatedAt || 0) > TEN_MIN) continue
        n++
      }
      setCount(n)
    })
    return () => off()
  }, [])

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(broadcasterUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, padding: 24,
          maxWidth: 720, width: '100%',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: CLUB.accentColor, fontWeight: 800 }}>
              📡 TOOL
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: CLUB.primaryColor, margin: '2px 0 0' }}>
              GPS Map Test
            </h2>
            <p style={{ fontSize: 12, color: '#4B5563', margin: '2px 0 0' }}>
              Same broadcaster URL for both options — pick the watcher view that
              fits where you're testing from.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, color: '#9CA3AF', cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close">×</button>
        </div>

        {/* Shared broadcaster QR + URL — applies to both options */}
        <div
          style={{
            display: 'flex', gap: 14, alignItems: 'center',
            padding: 12, background: '#F9FAFB',
            borderRadius: 12, border: '1px solid #E5E7EB',
          }}>
          <QRCodeCanvas value={broadcasterUrl} size={140} includeMargin />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color: '#6B7280' }}>
              BROADCASTER URL
            </div>
            <div style={{ marginTop: 2, fontSize: 14, fontWeight: 800, color: '#111827', wordBreak: 'break-all' }}>
              {broadcasterUrl.replace(/^https?:\/\//, '')}
            </div>
            <button
              type="button"
              onClick={copyUrl}
              style={{
                marginTop: 8, fontSize: 11, padding: '5px 10px', borderRadius: 6,
                background: 'rgba(0,0,0,0.06)', color: '#374151',
                border: '1px solid #D1D5DB', cursor: 'pointer',
              }}>
              {copied ? '✓ Copied' : 'Copy URL'}
            </button>
            <p style={{ marginTop: 8, fontSize: 11, color: '#6B7280', lineHeight: 1.4 }}>
              Testers scan this on iPhone, enter a name, and tap Start. Then pick
              a watcher below.
            </p>
          </div>
        </div>

        {/* Two-option chooser */}
        <div
          style={{
            display: 'grid', gap: 12, marginTop: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          }}>
          <WatcherOption
            label="Option A"
            title="PBCC Course Map Test"
            blurb="Use this AT Pine Bluff Country Club. Live broadcaster dots painted on the custom course image to verify on-course GPS accuracy."
            href={pbccWatcherUrl}
            buttonLabel="Open PBCC Watcher ↗"
            color={CLUB.primaryColor}
          />
          <WatcherOption
            label="Option B"
            title="Live Location Test (Google Maps)"
            blurb="Use this ANYWHERE. Real Google Maps view centered on the broadcaster — perfect for confirming the broadcasting system works from home or office before driving to PBCC."
            href={liveWatcherUrl}
            buttonLabel="Open Live Location ↗"
            color="#1F6FEB"
          />
        </div>

        <div
          style={{
            marginTop: 14, padding: '10px 12px',
            background: 'rgba(34,197,94,0.10)',
            border: `1px solid ${CLUB.accentColor}`,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46', letterSpacing: 1 }}>
            CURRENTLY ACTIVE
          </span>
          <span style={{ fontSize: 18, fontWeight: 900, color: CLUB.primaryColor }}>
            {count} tester{count === 1 ? '' : 's'}
          </span>
        </div>
      </div>
    </div>
  )
}

function WatcherOption({ label, title, blurb, href, buttonLabel, color }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${color}33`,
        background: `${color}08`,
        display: 'flex', flexDirection: 'column',
      }}>
      <div style={{ fontSize: 10, letterSpacing: 1.5, fontWeight: 800, color }}>
        {label}
      </div>
      <h3 style={{ fontSize: 15, fontWeight: 900, color: '#111827', margin: '4px 0 6px' }}>
        {title}
      </h3>
      <p style={{ fontSize: 12, color: '#4B5563', margin: 0, lineHeight: 1.45, flex: 1 }}>
        {blurb}
      </p>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          marginTop: 12, padding: '10px 12px', borderRadius: 10,
          background: color, color: '#fff',
          fontWeight: 800, letterSpacing: 0.5, fontSize: 13,
          textDecoration: 'none',
        }}>
        <MapPinIcon size={14} strokeWidth={2.2} />
        {buttonLabel}
      </a>
    </div>
  )
}


// One card in the home-page tournaments grid. Wrapping the body in a
// <Link> means clicks anywhere navigate — except the Delete button, which
// stops propagation and prevents the link's default so admin can prune
// from the list without first opening the tournament.
// Rolls the three PBCC 4-Ball sections into a new vertical order with a
// smooth FLIP slide whenever the current stage advances. Pure layout
// wrapper — the sections themselves are reused untouched. Measures each
// keyed child's position every render and, when it moved, plays it from
// its old spot to the new one.
function RollingSections({ children }) {
  const ref = useRef(null)
  const prev = useRef(new Map())
  useLayoutEffect(() => {
    const container = ref.current
    if (!container) return
    Array.from(container.children).forEach((node) => {
      const key = node.dataset.rollKey
      if (!key) return
      const rect = node.getBoundingClientRect()
      const last = prev.current.get(key)
      prev.current.set(key, rect)
      if (last) {
        const dy = last.top - rect.top
        if (Math.abs(dy) > 1) {
          node.style.transition = 'none'
          node.style.transform = `translateY(${dy}px)`
          // next frame: release to the new position with a smooth roll
          requestAnimationFrame(() => {
            node.style.transition = 'transform 520ms cubic-bezier(0.22, 1, 0.36, 1)'
            node.style.transform = 'translateY(0)'
          })
        }
      }
    })
  })
  return <div ref={ref} className="space-y-5">{children}</div>
}

// Registration formats — mirrors the qualifying-event list, PBCC 4-Ball first.
const REG_FORMATS = FOURBALL_ONLY
  ? ['4-Ball Best Ball']
  : ['4-Ball Best Ball', 'Stroke Play Individual', 'Scramble', 'Shamble', 'Alternate Shot', 'Team Cup', 'Stableford']

// Dashboard "Registration" section — everything happens right here, it never
// routes to the tournament page. "New Registration" names + creates the event
// in a popup, then the same flow flips straight to the registration surface in
// a popup. Each card opens Registration / Players inline in a modal too.
function RegistrationSection() {
  const navigate = useNavigate()
  const [tournaments, setTournaments] = useState({})
  const [creating, setCreating] = useState(false)
  // tournamentId whose registration list is open in the inline modal, or null.
  const [manageId, setManageId] = useState(null)

  useEffect(() => {
    const off = watchTournaments(setTournaments)
    return () => off()
  }, [])

  // The 4-Ball sidebar "New Registration" tab triggers the create flow here.
  useEffect(() => {
    const open = () => setCreating(true)
    window.addEventListener('pbcc4ball:new-registration', open)
    return () => window.removeEventListener('pbcc4ball:new-registration', open)
  }, [])

  const list = useMemo(() => (
    Object.entries(tournaments || {})
      .filter(([, v]) => v && typeof v === 'object')
      .map(([id, v]) => ({
        id,
        name: v?.info?.name || '(unnamed)',
        format: v?.info?.format || '',
        status: v?.info?.status || 'open',
        stage: v?.info?.stage || 'registration',
        isDemo: !!v?.info?.isDemo,
        isPractice: !!v?.info?.isPractice,
        isTestStation: !!v?.info?.isTestStation,
        qualifyingEventId: v?.info?.qualifyingEventId || null,
        createdAt: v?.info?.createdAt || 0,
        regCount: v?.registrations ? Object.keys(v.registrations).length : 0,
      }))
      // Registration shows 'registration'-stage events. Real demos are hidden,
      // but the flagged PRACTICE field is allowed through (badged) so staff can
      // run the whole live flow on it.
      // Once submitted to Qualifying (has a linked qualifyingEventId) the list
      // has moved on — hide it here so it shows only in Qualifying.
      .filter((t) => !t.isTestStation && !t.qualifyingEventId && (!t.isDemo || t.isPractice) && t.status !== 'closed' && t.stage === 'registration')
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  ), [tournaments])

  return (
    <section>
      <div className="mb-3">
        <div className="min-w-0">
          <span className="pbcc-gold-rule" aria-hidden="true" style={PBCC_PREMIUM ? { backgroundColor: '#C2A24E' } : FOURBALL_ONLY ? { backgroundColor: '#FFFFFF' } : undefined} />
          <h2 className="pbcc-h2 text-xl mt-1" style={(FOURBALL_ONLY || PBCC_PREMIUM) ? { color: '#FFFFFF' } : undefined}>Registration</h2>
          <p className={`text-sm mt-0.5 leading-snug ${FOURBALL_ONLY ? 'text-white' : 'text-gray-600'}`}>
            Set up entry for any event — name it, create it, then collect teams by self-reg link / QR, paste, CSV, or manual entry. Player rosters open here too.
          </p>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="pbcc-empty-card min-h-[220px]">
          <div
            className="hidden sm:flex w-52 md:w-64 lg:w-80 shrink-0 items-center justify-center relative"
            style={{
              backgroundImage: `url(/pbcc-fountain.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'contrast(1.08) saturate(1.12)',
            }}>
          </div>
          <div className="px-5 py-6 sm:px-8 flex flex-col gap-1.5 justify-center">
            <h3 className="text-lg font-extrabold text-gray-900">No open events.</h3>
            <p className="text-sm text-gray-600 leading-snug max-w-md">
              Use <b>New Registration</b> in the sidebar to name an event and start taking entries.
            </p>
          </div>
        </div>
      ) : (
        <div className={PBCC_PREMIUM ? 'pbcc-card-grid' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
          {list.map((t) => (
            <RegistrationCard key={t.id} t={t} onOpen={() => setManageId(t.id)} onOpenCenter={() => navigate(`/admin/t/${t.id}`)} />
          ))}
        </div>
      )}

      {creating ? (
        <NewRegistrationModal
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); setManageId(id) }}
        />
      ) : null}

      {manageId ? (
        <RegistrationManageModal
          tournamentId={manageId}
          tournament={tournaments?.[manageId] || null}
          onClose={() => setManageId(null)}
        />
      ) : null}
    </section>
  )
}

function RegistrationCard({ t, onOpen, onOpenCenter }) {
  // Whole card opens the registration; the Delete control clears a stale one
  // (mirrors the tournament card's Delete). Deleting removes the tournament
  // node + its registrations, so the card leaves the section immediately.
  const onDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!confirm(`Delete "${String(t.name || 'this registration').split('\n')[0]}" and its ${t.regCount} registrations? This cannot be undone.`)) return
    deleteTournament(t.id).catch(() => {})
  }
  // Open the FULL tournament center (all tabs — Flights & Teams, Tee Times, TV,
  // Bracket, etc.) for this event WITHOUT it being approved/built, so everything
  // can be set up ahead of time. Stops propagation so it doesn't also open the
  // roster modal (the card's default click).
  const onCenter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onOpenCenter?.()
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      className="block w-full text-left rounded-2xl p-4 transition hover:shadow-lg active:scale-[0.99] cursor-pointer"
      style={PREM_CARD || { backgroundColor: '#FFFFFF', border: '1px solid #123D2655', boxShadow: '0 6px 18px rgba(0,0,0,0.08)' }}>
      {PBCC_PREMIUM ? (
        <>
          <div className="flex items-center gap-1.5 mb-2">
            {t.isPractice ? (
              <span className="text-sm font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Practice</span>
            ) : null}
            <span className="pill-green">{(t.status || 'open').toUpperCase()}</span>
          </div>
          <h3 className="font-bold text-sm leading-tight" style={{ color: '#123D26' }}>{String(t.name || '').split('\n')[0]}</h3>
        </>
      ) : (
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-extrabold text-base" style={{ color: '#123D26' }}>{String(t.name || '').split('\n')[0]}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            {t.isPractice ? (
              <span className="text-sm font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Practice</span>
            ) : null}
            <span className="pill-green">{t.status || 'open'}</span>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
        {PBCC_PREMIUM ? <Flag size={13} strokeWidth={2.2} color="#C2A24E" /> : null}
        <span>{t.format || '4-Ball Best Ball'}</span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 text-sm" style={{ color: '#123D26' }}>
        <span><b>{t.regCount}</b> registered</span>
        <div className="flex items-center gap-2">
          <button
            onClick={onCenter}
            className="text-sm font-bold px-2.5 py-1 rounded-lg transition active:scale-[0.98]"
            style={{ color: '#FFFFFF', background: '#123D26', border: '1px solid #123D26' }}
            title="Open the full tournament center — all tabs (Flights & Teams, Tee Times, TV, Bracket…) — no approval needed">
            ⛳ Tournament Center →
          </button>
          <button
            onClick={onDelete}
            className="text-sm font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-0.5 rounded transition"
            title="Delete registration">
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// Inline manager — renders the existing Registration surface inside a dashboard
// popup so the admin never leaves the page. `tournament` is fed live from the
// section's watchTournaments listener, so the registered count updates as
// entries come in. Registration only lists who has registered online — no
// flighting, no player roster (setup + qualifying handle that later).
function RegistrationManageModal({ tournamentId, tournament, onClose }) {
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!tournament) {
    return (
      <Modal title="Loading…" onClose={onClose}>
        <p className="text-sm text-gray-600">Loading…</p>
      </Modal>
    )
  }

  const regs = tournament.registrations || {}
  const regCount = Object.keys(regs).length

  // Save the current registered field as a reusable list. Rows match the
  // registration shape so "From Prior Event" can load them straight back.
  const onSave = async () => {
    const name = window.prompt('Save this list as:', tournament.info?.name || 'Saved List')
    if (name == null) return
    setSaving(true)
    try {
      const teams = Object.values(regs).map((r) => ({
        teamName: r?.teamName || 'Team',
        players: Array.isArray(r?.players) ? r.players : [r?.player1, r?.player2].filter(Boolean),
        email: r?.email || null,
        phone: r?.phone || null,
      }))
      await saveRegistrationList({ name, format: tournament.info?.format, teams })
      // Save List saves the reusable list AND closes — the registration itself
      // is already live-saved; this is the "I'm done here" action.
      onClose()
    } catch (e) {
      window.alert(e?.message || 'Could not save list.')
      setSaving(false)
    }
  }

  // Delete the whole registration event (the tournament + its registrations).
  // When locked, the access code is required first (accidental-delete guard).
  const onDelete = async () => {
    if (!confirmAccessCodeIfLocked(tournament.info?.locked)) return
    if (!window.confirm(`Delete "${(tournament.info?.name || 'this event').split('\n')[0]}" and all ${regCount} registrations? This cannot be undone.`)) return
    try {
      await deleteTournament(tournamentId)
      onClose()
    } catch (e) {
      window.alert(e?.message || 'Could not delete.')
    }
  }
  const onToggleLock = () => updateTournamentInfo(tournamentId, { locked: !tournament.info?.locked })

  // Submit-to-Qualify (needs-to-qualify lists): reuse the exact send-to-
  // qualifying logic (createQualifyingEventFromRegistration + stamp the
  // qualifyingEventId back on the node), then just close. Staying on the PBCC
  // 4-Ball tab lets the sections auto-roll to Qualifying (State 2) with the
  // list already loaded — no manual "New Qualifying Event → pick" step.
  const needsQualifying = tournament.info?.needsQualifying !== false
  const onSubmitToQualify = async () => {
    setSubmitting(true)
    try {
      const eventId = await createQualifyingEventFromRegistration(tournamentId, tournament.info, regs)
      await updateTournamentInfo(tournamentId, { qualifyingEventId: eventId })
      onClose()
    } catch (e) {
      window.alert(e?.message || 'Could not submit to Qualifying.')
      setSubmitting(false)
    }
  }

  return (
    <Modal title={null} onClose={onClose} size="xl">
      <div className="mb-4">
        <EventTitle info={tournament.info} label="Registration" />
      </div>
      {/* "Needs to qualify" toggle — DEFAULTS ON (most events qualify). This
          flag is the ONLY thing that makes the list show in Qualifying's
          "New Qualifying Event" dropdown. The registration side never sends;
          the Qualifying side pulls. (Merged into info, name/dates untouched.) */}
      <label
        className="mb-4 flex items-center justify-between gap-3 rounded-lg border px-3 py-2 cursor-pointer"
        style={{ borderColor: 'rgba(18, 61, 38,0.4)', background: '#FFFFFF' }}>
        <span>
          <span className="block text-sm font-bold text-gray-800">This list needs to qualify</span>
          <span className="block text-sm text-gray-500">
            On (default) → appears in Qualifying → New Qualifying Event to run a qualifier. Off → skips straight to New Tournament.
          </span>
        </span>
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0"
          checked={tournament.info?.needsQualifying !== false}
          onChange={(e) => updateTournamentInfo(tournamentId, { needsQualifying: e.target.checked })}
        />
      </label>

      <div className="max-h-[70vh] overflow-y-auto pr-1">
        <RegistrationTab tournamentId={tournamentId} tournament={tournament} />
      </div>
      <div className="flex items-center gap-2 pt-3 mt-3 border-t" style={{ borderColor: 'rgba(18, 61, 38,0.30)' }}>
        {needsQualifying ? (
          <button
            type="button"
            onClick={onSubmitToQualify}
            disabled={submitting || regCount === 0}
            className="btn-primary text-sm">
            {submitting ? 'Submitting…' : '⛳ Submit to Qualify'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSave}
            disabled={saving || regCount === 0}
            className="btn-primary text-sm">
            {saving ? 'Saving…' : '💾 Save List & Close'}
          </button>
        )}
        <button
          type="button"
          onClick={onToggleLock}
          className="btn-secondary text-sm"
          style={{ marginLeft: 'auto' }}
          title={tournament.info?.locked ? 'Locked — delete needs the access code' : 'Lock to guard against accidental delete'}>
          {tournament.info?.locked ? '🔒 Locked' : '🔓 Lock'}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="btn-ghost text-sm"
          style={{ color: '#B91C1C' }}>
          🗑 Delete List{tournament.info?.locked ? ' 🔒' : ''}
        </button>
      </div>
    </Modal>
  )
}

// Players-per-team choices. 1 = individual entry, 2 = the 4-Ball default,
// 3/4 for larger team formats. Drives info.teamSize, which the public
// self-reg form reads to render that many player fields.
const TEAM_SIZE_OPTIONS = [
  { value: 2, label: 'Team of 2 (pairs)' },
  { value: 1, label: 'Individual (1 player)' },
  { value: 3, label: 'Team of 3' },
  { value: 4, label: 'Team of 4' },
]

function NewRegistrationModal({ onClose, onCreated }) {
  // PBCC 4-Ball invitational defaults — this tab is dedicated to that one event,
  // so the New Event form opens pre-filled. The admin only tweaks the year
  // number + dates and clicks Create, instead of filling it from scratch.
  const [name, setName] = useState('62nd Annual Pine Bluff Country Club\nFour-Ball Match Play Invitational\nJuly 30th – Aug 2nd, 2026')
  const [format, setFormat] = useState(REG_FORMATS[0]) // 4-Ball Best Ball
  const [teamSize, setTeamSize] = useState(2)           // Team of 2 (pairs)
  const [maxTeams, setMaxTeams] = useState('104')
  const [startDate, setStartDate] = useState('2026-07-30')
  const [endDate, setEndDate] = useState('2026-08-02')
  const [entryDeadline, setEntryDeadline] = useState('2026-07-23')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [mode, setMode] = useState('new') // 'new' | 'demo'
  const [demoBusy, setDemoBusy] = useState(false)

  const entryWord = teamSize === 1 ? 'players' : 'teams'
  // Preserve the typed line breaks (each line = a title line on the board);
  // just tidy spaces within each line and drop blank lines.
  const cleanName = name
    .split('\n')
    .map((l) => l.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n')

  const submit = async () => {
    setBusy(true)
    setErr('')
    try {
      const today = new Date().toISOString().slice(0, 10)
      const info = {
        name: cleanName,
        format,
        date: startDate || today,
        endDate: endDate || null,
        course: CLUB.course?.name || '',
        teamSize: Number(teamSize),
        maxTeams: maxTeams === '' ? null : Number(maxTeams),
        entryDeadline: entryDeadline || null,
        stage: 'registration', // Phase 1: a new list starts in Registration and stays there.
        needsQualifying: true,  // defaults ON (most events qualify); admin can turn it off.
      }
      const id = await createTournament(info)
      onCreated(id)
    } catch (e) {
      setErr(e?.message || 'Could not create.')
      setBusy(false)
    }
  }

  // Demo 96 — one click loads the existing fake 96-team roster and creates the
  // registration. Flagged isDemo/isPractice so it never mixes with real data;
  // from here it behaves exactly like a real registration (Submit to Qualify →
  // auto-scored at Qualifying → Approve & Lock → Build). Reuses the existing
  // fake roster + auto-score path — nothing rebuilt.
  const loadDemo96 = async () => {
    setDemoBusy(true)
    setErr('')
    try {
      const id = await createTournament({
        name: 'Demo — 96-Team Field\n62nd Annual Pine Bluff Four-Ball\nJuly 30th – Aug 2nd, 2026',
        format: '4-Ball Best Ball',
        course: CLUB.course?.name || 'Pine Bluff Country Club',
        date: '2026-07-30',
        endDate: '2026-08-02',
        teamSize: 2,
        maxTeams: 96,
        stage: 'registration',
        needsQualifying: true,
        isDemo: true,     // flagged — kept out of My Tournaments / real counts
        isPractice: true, // flows through the real screens (badged); auto-scores at Qualifying
      })
      // demos.js FULL_TOURNAMENT_TEAMS_96 → 12 flights of 8, realistic 68–85 spread.
      await bulkAddRegistrations(id, demoRegistrationRows(12).map((t) => ({
        teamName: t.teamName,
        players: t.players,
        qualifyingScore: t.qualifyingScore,
      })))
      onCreated(id)
    } catch (e) {
      setErr(e?.message || 'Could not load the demo field.')
      setDemoBusy(false)
    }
  }

  return (
    <Modal title="New Registration" onClose={onClose} size="lg">
      <div className="space-y-3">
        {/* The "Demo 96" loader is a testing tool — never on the 4-Ball club site
            (it would let the club load a fake 96-team field). */}
        {!FOURBALL_ONLY && (
        <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(18, 61, 38,0.08)' }}>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`flex-1 text-sm font-bold py-1.5 rounded-md transition ${mode === 'new' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}>
            New Event
          </button>
          <button
            type="button"
            onClick={() => setMode('demo')}
            className={`flex-1 text-sm font-bold py-1.5 rounded-md transition ${mode === 'demo' ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}>
            🧪 Demo 96
          </button>
        </div>
        )}

        {mode === 'demo' && !FOURBALL_ONLY ? (
          <div className="rounded-xl px-5 py-6 space-y-3" style={PREM_PANEL || { border: '1px solid rgba(18, 61, 38,0.5)', background: '#FFFFFF' }}>
            <h3 className="font-extrabold text-base text-gray-900">Demo 96-Team Field</h3>
            <p className="text-sm text-gray-600 leading-snug">
              Loads a flagged 96-team fake field (fake names, realistic qualifying spread). From here it behaves exactly like a real registration — <b>Submit to Qualify</b>, then all 96 arrive in Qualifying already scored, ready to <b>Approve &amp; Lock</b> and build. Flagged demo/practice — never mixes with real data.
            </p>
            {err ? <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>{err}</p> : null}
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" className="btn-ghost" onClick={onClose} disabled={demoBusy}>Cancel</button>
              <button type="button" className="btn-primary" onClick={loadDemo96} disabled={demoBusy}>
                {demoBusy ? 'Loading…' : 'Load 96 Demo Teams'}
              </button>
            </div>
          </div>
        ) : (
        <>
        {/* Wall-board title preview — each name line is its own row and the
            ordinal date range sits centered below (shared EventTitle). */}
        <div
          className="rounded-xl px-5 py-5"
          style={PREM_PANEL || { border: '1px solid rgba(18, 61, 38,0.5)', background: '#FFFFFF' }}>
          <EventTitle info={{ name: cleanName, date: startDate, endDate }} />
        </div>

        <Field label="Event Name">
          <textarea
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            rows={3}
            style={{ resize: 'vertical', minHeight: 72 }}
            placeholder={'62nd Annual Pine Bluff Country Club\nFour-Ball Match Play Invitational'}
            autoFocus
          />
          <p className="text-sm text-gray-500 mt-1 leading-snug">
            Each line here becomes a line on the title board. Press <b>Enter</b> to split the name across two lines; the dates render on their own line below.
          </p>
        </Field>
        <Field label="Format">
          <select value={format} onChange={(e) => setFormat(e.target.value)} className="input">
            {REG_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Register by">
          <select value={teamSize} onChange={(e) => setTeamSize(Number(e.target.value))} className="input">
            {TEAM_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
          </Field>
          <Field label="End Date">
            <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} className="input" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Max ${entryWord === 'players' ? 'Players' : 'Teams'}`}>
            <input
              type="number"
              min="1"
              value={maxTeams}
              onChange={(e) => setMaxTeams(e.target.value)}
              className="input"
              placeholder="No limit"
            />
          </Field>
          <Field label="Deadline">
            <input
              type="date"
              value={entryDeadline}
              onChange={(e) => setEntryDeadline(e.target.value)}
              className="input"
            />
          </Field>
        </div>
        <p className="text-sm text-gray-500 leading-snug">
          Registration closes itself once it hits the max {entryWord} or the deadline passes. You can still edit or add {entryWord} by hand anytime.
        </p>
        {err ? <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>{err}</p> : null}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy || !cleanName}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
        </>
        )}
      </div>
    </Modal>
  )
}

// ── Right rail (TEST/premium only) ──────────────────────────────────────────
// Balances the full-height left sidebar: a fixed-width (w-80), right-anchored
// column that never drifts. Quick-Action shortcuts to existing pages + the two
// on-course rule cards (content reused from CLUB.rulesOfPlay / CLUB.clubRules).
// View-only shortcuts only — no tournament-changing actions in the quick tiles,
// so an accidental tap can never alter a field. Photo Screen jumps straight to
// what the TVs are currently displaying.
const RAIL_ACTIONS = [
  { label: 'Bracket & Tee Times', to: '/tv/brackets',   Icon: Shuffle },
  { label: 'Player Message',      to: '/admin/player-message', Icon: MessageSquare },
  { label: 'Photo Screen',        to: '/tv/photos',     Icon: ImageIcon },
  { label: 'Course Map (GPS)',    to: '/tv/scrollmap',  Icon: MapPinIcon },
  { label: 'TV Control',          to: '/tvcontrol',     Icon: TvIcon },
  { label: 'Calcutta',            to: '/admin/calcutta', Icon: DollarSign },
  { label: 'Live Leaderboard',    to: '/leaderboard',   Icon: ListOrdered },
]

function RailLabel({ children }) {
  return (
    <div className="text-sm font-bold uppercase tracking-[0.18em] mb-2.5" style={{ color: '#C2A24E' }}>
      {children}
    </div>
  )
}

function RulesRailCard({ title, items }) {
  const [open, setOpen] = useState(false)
  const preview = items.slice(0, 3)
  const hasMore = items.length > preview.length
  const shown = open ? items : preview
  return (
    <div className="rounded-2xl p-4" style={{ background: '#F7F2E6', border: '1.5px solid #C2A24E', boxShadow: '0 10px 28px rgba(0,0,0,0.28)' }}>
      <h3 className="text-base mb-2.5" style={{ fontFamily: 'Cinzel, serif', fontWeight: 700, color: '#123D26' }}>{title}</h3>
      <ul className="space-y-2">
        {shown.map((line, i) => (
          <li key={i} className="flex gap-2 text-sm leading-snug" style={{ color: '#3A3A2E' }}>
            <span aria-hidden style={{ color: '#C2A24E', lineHeight: 1.35 }}>◆</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-sm font-bold tracking-wide"
          style={{ color: '#9C7B2E' }}>
          {open ? 'Show Less ↑' : 'View Full Rules →'}
        </button>
      )}
    </div>
  )
}

function AdminRightRail() {
  return (
    <aside className="pbcc-admin-rail flex flex-col gap-7 py-4 sm:py-8 pr-4 sm:pr-8">
      <section>
        <RailLabel>Quick Actions</RailLabel>
        <div className="grid grid-cols-2 gap-2.5">
          {RAIL_ACTIONS.map(({ label, to, Icon }) => (
            <Link
              key={label}
              to={to}
              className="flex flex-col items-start gap-2 rounded-xl px-3 py-3 transition hover:-translate-y-0.5"
              style={{ background: '#0E281D', border: '1px solid rgba(194,162,78,0.7)', color: '#F7F2E6', textDecoration: 'none' }}>
              <Icon size={18} strokeWidth={2} color="#C2A24E" />
              <span className="text-sm font-semibold leading-tight">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(194,162,78,0.75), transparent)' }} />

      <section className="flex flex-col gap-4">
        <RailLabel>On-Course Rules</RailLabel>
        <RulesRailCard title="Rules of Play" items={CLUB.rulesOfPlay} />
        <RulesRailCard title="Club Rules" items={CLUB.clubRules} />
      </section>
    </aside>
  )
}

function TournamentListCard({ t }) {
  const onDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!confirm(`Delete ${t.name || 'this tournament'}? This cannot be undone.`)) return
    deleteTournament(t.id).catch(() => {})
  }
  if (PBCC_PREMIUM) {
    const badge = (t.status || 'open').toUpperCase()
    return (
      <Link
        to={`/admin/t/${t.id}`}
        className={`pbcc-demo-card hover:shadow-lg transition ${(t.isDemo || t.isPractice) ? 'border-2 border-dashed border-amber-300' : ''}`}>
        <div className="pbcc-demo-card-body">
          {/* Badge top-left, above the title. */}
          <div className="flex items-center gap-1.5 mb-2">
            {(t.isDemo || t.isPractice) ? (
              <span className="text-sm font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
                {t.isPractice ? 'Practice' : 'Demo'}
              </span>
            ) : null}
            <span className="pill-green">{badge}</span>
          </div>
          <h3 className="font-bold text-sm leading-tight" style={{ color: '#123D26', whiteSpace: 'pre-line' }}>{t.name}</h3>
          <div className="mt-2.5 space-y-1.5 text-sm" style={{ color: '#123D26' }}>
            <div className="flex items-center gap-2"><MapPinIcon size={14} strokeWidth={2.2} color="#C2A24E" /><span>{t.course || 'Course TBD'}</span></div>
            <div className="flex items-center gap-2"><Calendar size={14} strokeWidth={2.2} color="#C2A24E" /><span>{t.date || 'Date TBD'}</span></div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Flag size={14} strokeWidth={2.2} color="#C2A24E" /><span>{t.format || '4-Ball Best Ball'}</span></div>
              <button
                onClick={onDelete}
                className="text-sm font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-0.5 rounded transition"
                title="Delete tournament">
                Delete
              </button>
            </div>
          </div>
        </div>
      </Link>
    )
  }
  return (
    <Link
      to={`/admin/t/${t.id}`}
      className={`pbcc-demo-card hover:shadow-lg transition ${(t.isDemo || t.isPractice) ? 'border-2 border-dashed border-amber-300 bg-amber-50/40' : ''}`}>
      <div className="pbcc-demo-card-body">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold text-lg">{t.name}</h3>
        <div className="flex items-center gap-1.5">
          {(t.isDemo || t.isPractice) ? (
            <span className="text-sm font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
              {t.isPractice ? 'Practice' : 'Demo'}
            </span>
          ) : null}
          <span className="pill-green">{t.status || 'open'}</span>
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-1">{t.course || 'Course TBD'}</p>
      <p className="text-sm text-gray-500 mt-1">{t.date || 'Date TBD'}</p>
      <div className="mt-2 flex justify-between items-center">
        <p className="text-sm text-gray-400">{t.format || '4-Ball Best Ball'}</p>
        <button
          onClick={onDelete}
          className="text-sm font-semibold text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-0.5 rounded transition"
          title="Delete tournament">
          Delete
        </button>
      </div>
      </div>
    </Link>
  )
}

// Default teams-per-hole at shotgun based on scramble team size. Aim for
// 4 players per group whenever possible (the standard golf foursome).
//   1-man → 4 teams per hole (4 players)
//   2-man → 2 teams per hole (4 players)
//   3-man → 1 team per hole (3 players — odd team size leaves no clean way to fit 4)
//   4-man → 1 team per hole (4 players)
export function scrambleDefaultPerHole(teamSize) {
  const n = Number(teamSize) || 4
  if (n <= 1) return 4
  if (n === 2) return 2
  return 1
}

// Admin can override down (never up) — keeps players-per-hole ≤ 4.
export function scramblePerHoleOptions(teamSize) {
  const def = scrambleDefaultPerHole(teamSize)
  const opts = []
  for (let n = def; n >= 1; n--) opts.push(n)
  return opts
}

function explainPerHole(teamSize, perHole) {
  const players = Number(teamSize) * Number(perHole)
  return `${perHole} team${perHole === 1 ? '' : 's'} of ${teamSize} share a starting hole at shotgun (${players} player${players === 1 ? '' : 's'} per hole).`
}

// Tournament format presets — one tile per visible format. Picking one
// fills the format string + playStyle + eventType on the new tournament
// in a single click. `Icon` is a lucide component the TypePicker renders;
// `desc` is the one-line tile blurb. The Edit-Tournament modal still
// lets directors flip playStyle (match/stroke) after the fact.
const ALL_FORMAT_PRESETS = [
  {
    // PBCC's signature event: 4-Ball Best Ball with a qualifying round,
    // auto-flighted by qualifying score, calcutta + brackets enabled.
    // info.pbcc4Ball:true is the gate every downstream surface (admin
    // QualifyingTab, AI builder, /qualify scorecard) reads to know this
    // is the PBCC profile vs. a generic 4-Ball Best Ball.
    key: 'pbcc4ball',
    label: 'PBCC 4-Ball',
    desc: 'Pine Bluff signature: qualifying round → 8-team flights → match-play brackets + Calcutta.',
    Icon: TrophyIcon,
    info: { format: '4-Ball Best Ball', playStyle: 'match', eventType: 'club', pbcc4Ball: true, qualifyingMethod: 'round' },
  },
  {
    key: 'stroke',
    label: 'Stroke Play',
    desc: 'Individual scores, lowest total wins.',
    Icon: ListOrdered,
    info: { format: 'Stroke Play Individual', playStyle: 'stroke', eventType: 'club' },
  },
  {
    key: 'scramble',
    label: 'Scramble',
    desc: 'Team picks best shot each hole.',
    Icon: Users,
    info: { format: 'Scramble', playStyle: 'stroke', eventType: 'club' },
  },
  {
    key: 'shamble',
    label: 'Shamble',
    desc: 'Team picks best drive, each plays own ball in.',
    Icon: Users,
    info: { format: 'Shamble', playStyle: 'stroke', eventType: 'club' },
  },
  {
    key: 'fourball',
    label: '4-Ball Best Ball',
    desc: 'Each player plays own ball, best score per hole counts.',
    Icon: Target,
    info: { format: '4-Ball Best Ball', playStyle: 'match', eventType: 'club' },
  },
  {
    key: 'altshot',
    label: 'Alternate Shot',
    desc: 'Teammates alternate hitting the same ball.',
    Icon: Shuffle,
    info: { format: 'Alternate Shot', playStyle: 'stroke', eventType: 'club' },
  },
  {
    key: 'stableford',
    label: 'Stableford',
    desc: 'Points awarded per hole based on score vs par.',
    Icon: Award,
    info: { format: 'Stableford', playStyle: 'stroke', eventType: 'club' },
  },
  {
    key: 'teamcup',
    label: 'Team Cup',
    desc: 'Team match play with points per hole.',
    Icon: TrophyIcon,
    info: { format: 'Team Cup', playStyle: 'match', eventType: 'club' },
  },
]

// 4-Ball-only site keeps ONLY the two 4-Ball presets (PBCC 4-Ball signature +
// generic 4-Ball Best Ball); every other format is removed from the create flow.
const FORMAT_PRESETS = FOURBALL_ONLY
  ? ALL_FORMAT_PRESETS.filter((p) => p.key === 'pbcc4ball' || p.key === 'fourball')
  : ALL_FORMAT_PRESETS

// Collapsible per-year archive of closed tournaments. Click a row to open
// the standard tournament detail page (Results tab shows the final
// standings).
function PastTournaments({ closed }) {
  const [open, setOpen] = useState({}) // year → bool

  // Group closed tournaments by year (date first, then closedAt fallback).
  const byYear = closed.reduce((acc, t) => {
    const y = (t.date || '').slice(0, 4) || (t.closedAt ? new Date(t.closedAt).getFullYear() : (t.createdAt ? new Date(t.createdAt).getFullYear() : 'Other'))
    if (!acc[y]) acc[y] = []
    acc[y].push(t)
    return acc
  }, {})
  const years = Object.keys(byYear).sort().reverse()

  // Default: latest year expanded.
  useEffect(() => {
    if (years.length > 0 && Object.keys(open).length === 0) {
      setOpen({ [years[0]]: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years.length])

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold mb-3">Past Tournaments</h2>
      <div className="card divide-y divide-gray-100 p-0 overflow-hidden">
        {years.map((y) => {
          const list = byYear[y]
          const expanded = !!open[y]
          return (
            <div key={y}>
              <button
                onClick={() => setOpen((s) => ({ ...s, [y]: !s[y] }))}
                className="w-full flex justify-between items-center px-5 py-3 hover:bg-gray-50">
                <span className="font-extrabold text-green-deep">
                  {expanded ? '▼' : '▶'} {y}
                </span>
                <span className="text-sm text-gray-500">{list.length} tournament{list.length === 1 ? '' : 's'}</span>
              </button>
              {expanded ? (
                <ul className="px-5 pb-3">
                  {list.map((t) => <PastRow key={t.id} t={t} />)}
                </ul>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function PastRow({ t }) {
  // Pull a winner string from the stored results snapshot, if present.
  const flightSummary = []
  const flightResults = t.results?.flights || {}
  for (const [, fr] of Object.entries(flightResults)) {
    if (fr?.first?.teamName) {
      flightSummary.push(`${fr.first.teamName} (${fr.name || 'Flight'})`)
    }
  }
  const winnerLine = flightSummary.length > 0
    ? `Won by ${flightSummary.join(' · ')}`
    : 'Final results inside'

  return (
    <li>
      <Link
        to={`/admin/t/${t.id}`}
        className="block py-2.5 hover:bg-gray-50 -mx-2 px-2 rounded">
        <div className="flex justify-between items-baseline gap-3">
          <span className="font-bold text-sm">{t.name}</span>
          <span className="text-sm text-gray-500 shrink-0">{t.date || ''}</span>
        </div>
        <p className="text-sm text-gray-600 mt-0.5">{winnerLine}</p>
      </Link>
    </li>
  )
}

// Step 1 of the create flow. Renders the FORMAT_PRESETS as tappable tiles
// in a responsive grid. Director picks one → onPick(key) advances to the
// regular CreateModal with that preset locked in. Cancel returns to the
// dashboard with no tournament created.
function BuildPickerModal({ onClose, onStartFresh }) {
  const [tournaments, setTournaments] = useState({})
  const [qualifyingEvents, setQualifyingEvents] = useState({})
  const [sel, setSel] = useState('')
  const [rosterFor, setRosterFor] = useState(null) // { item, field } → the roster/AI modal
  useEffect(() => {
    const offT = watchTournaments(setTournaments)
    const offQ = watchQualifyingEvents(setQualifyingEvents)
    return () => { offT(); offQ() }
  }, [])

  const ready = useMemo(() => {
    const out = []
    for (const [id, v] of Object.entries(tournaments || {})) {
      if (!v || typeof v !== 'object') continue
      const info = v.info || {}
      if ((info.isDemo && !info.isPractice) || info.isTestStation) continue // practice field flows through
      if ((info.stage || 'registration') !== 'registration') continue
      if (info.status === 'closed') continue
      const regCount = v.registrations ? Object.keys(v.registrations).length : 0
      // needsQualifying defaults ON (missing = ON); only an explicit false skips.
      if (info.needsQualifying === false) {
        out.push({ id, info, count: regCount, kind: 'skip', qualifyingEventId: null })
      } else if (info.qualifyingEventId) {
        const qe = qualifyingEvents?.[info.qualifyingEventId]
        const approved = qe?.teams
          ? Object.values(qe.teams).filter((t) => t && (t.approved === true || t.status === 'approved')).length
          : 0
        const locked = (qe?.info?.status || 'open') === 'closed'
        if (locked && approved > 0) out.push({ id, info, count: approved, kind: 'qualified', qualifyingEventId: info.qualifyingEventId })
      }
    }
    return out.sort((a, b) => (b.info.createdAt || 0) - (a.info.createdAt || 0))
  }, [tournaments, qualifyingEvents])

  const selItem = ready.find((r) => r.id === sel) || null
  const firstLine = (n) => String(n || '(unnamed)').split('\n')[0]

  // "Review field & build" — pull the field (with qualifying scores when the
  // event qualified) and open the original roster/AI modal. Nothing is promoted
  // yet; that happens when the pro clicks Build with AI / Build Manually.
  const onReview = () => {
    if (!selItem) return
    const qe = selItem.qualifyingEventId ? qualifyingEvents?.[selItem.qualifyingEventId] : null
    let field = []
    if (qe?.teams) {
      field = Object.values(qe.teams)
        .filter((t) => t && (t.approved === true || t.status === 'approved'))
        .map((t) => ({
          teamName: t.teamName || [t.player1, t.player2].filter(Boolean).join(' / ') || 'Team',
          qualifyingScore: Number.isFinite(Number(t.score)) ? Number(t.score) : (Number.isFinite(Number(t.qualifyingScore)) ? Number(t.qualifyingScore) : null),
        }))
    } else {
      const regs = tournaments?.[selItem.id]?.registrations || {}
      field = Object.values(regs).map((r) => ({
        teamName: r?.teamName || (r?.players || []).join(' / ') || 'Team',
        qualifyingScore: Number.isFinite(Number(r?.qualifyingScore)) ? Number(r.qualifyingScore) : null,
      }))
    }
    setRosterFor({ item: selItem, field })
  }

  // The field/scores pop-up with "Build with AI" front and center. Build
  // promotes the SAME node (single-node model) then enters the AI/manual setup.
  if (rosterFor) {
    const it = rosterFor.item
    return (
      <TournamentCreatedRosterModal
        tournamentId={it.id}
        approvedTeams={rosterFor.field}
        tournamentName={firstLine(it.info.name)}
        qualifyingEventName={it.kind === 'qualified' ? firstLine(it.info.name) : ''}
        titleText="Ready to Build"
        introText={<>This field is ready — <b>{rosterFor.field.length}</b> team{rosterFor.field.length === 1 ? '' : 's'}{it.kind === 'qualified' ? ' with qualifying scores' : ''}. <b>Build</b> promotes this same list into the live tournament (no duplicate) and runs the standard PBCC 4-Ball flight/wave build. Pick a setup path:</>}
        onBuild={async () => {
          // Reuse the one shared build path. 'qualified' → full PBCC build from
          // the locked field; 'skip' (needsQualifying=false) → promote only.
          if (it.kind === 'qualified') {
            await buildTournamentFromQualifying(it.id, it.qualifyingEventId, { isPractice: it.info.isPractice })
          } else {
            await promoteRegistrationToTournament(it.id, it.qualifyingEventId)
          }
        }}
        onClose={() => setRosterFor(null)}
      />
    )
  }

  return (
    <Modal title="New Tournament" onClose={onClose} size="lg">
      <div className="space-y-3">
        {ready.length > 0 ? (
          <>
            <p className="text-sm text-gray-700">
              Build a ready event — its name, dates, and field carry over. No re-entry.
            </p>
            <select className="input" value={sel} onChange={(e) => setSel(e.target.value)} autoFocus>
              <option value="">Choose a ready event…</option>
              {ready.map((r) => (
                <option key={r.id} value={r.id}>
                  {firstLine(r.info.name)} — {r.kind === 'qualified' ? `${r.count} approved (qualified)` : `${r.count} team${r.count === 1 ? '' : 's'} (no qualifying)`}
                </option>
              ))}
            </select>
            {selItem ? (
              <div className="rounded-xl px-5 py-4" style={PREM_PANEL || { border: '1px solid rgba(18, 61, 38,0.5)', background: '#FFFFFF' }}>
                <EventTitle info={selItem.info} label={selItem.kind === 'qualified' ? 'Qualified field · ready to build' : 'Ready to build'} />
              </div>
            ) : null}
            <button type="button" className="btn-primary w-full" disabled={!sel} onClick={onReview}>
              Review field & build →
            </button>
            <div className="text-center text-sm text-gray-400">— or —</div>
          </>
        ) : (
          <p className="text-sm text-gray-600 leading-snug">
            Nothing is ready to build yet. Create a <b>Registration</b> first (and, if it needs qualifying, <b>Approve &amp; Lock</b> the field) — then it shows up here. Or start a fresh tournament.
          </p>
        )}
        <button type="button" className="btn-secondary w-full" onClick={onStartFresh}>
          Start a fresh tournament
        </button>
      </div>
    </Modal>
  )
}

function TypePickerModal({ onClose, onPick }) {
  return (
    <Modal onClose={onClose} title="Choose Tournament Type" size="lg">
      <p className="text-sm text-gray-600 mb-3 leading-snug">
        Pick the format that matches how you want to score this tournament.
        You'll fill in the name, date, and other details on the next step.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FORMAT_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onPick(p.key)}
            className="text-left p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-[#123D26] hover:shadow-md hover:bg-[#F2F7F3] transition flex items-start gap-3 group">
            <div
              className="flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 44, height: 44,
                background: '#123D26', color: '#123D26',
              }}>
              {p.Icon ? <p.Icon size={22} strokeWidth={1.8} /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-extrabold text-sm text-gray-900 group-hover:text-[#123D26]">{p.label}</div>
              <div className="text-sm text-gray-600 mt-0.5 leading-snug">{p.desc}</div>
            </div>
          </button>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  )
}

function CreateModal({ onClose, onSave, lockedPreset = null }) {
  const [name, setName] = useState('')
  const [date, setDate] = useState('')
  const [course, setCourse] = useState('')
  // Optional "Import From Qualifying Event" dropdown. '' = None. When
  // set, the parent handler reads approved teams from that event and
  // writes them into /tournaments/{newId}/registrations after create.
  const [importEventId, setImportEventId] = useState('')
  const [qualifyingEvents, setQualifyingEvents] = useState({})
  useEffect(() => {
    const off = watchQualifyingEvents(setQualifyingEvents)
    return () => off()
  }, [])
  const qualifyingOptions = (() => {
    return Object.entries(qualifyingEvents || {}).map(([id, v]) => {
      const teams = v?.teams || {}
      // Mirror the import filter — count both status:'approved' and
      // approved:true so seeded data and live admin-approved data are
      // both visible in the dropdown.
      const approved = Object.values(teams).filter((t) => t && (t.status === 'approved' || t.approved === true)).length
      return { id, name: v?.info?.name || '(unnamed)', approved }
    }).sort((a, b) => a.name.localeCompare(b.name))
  })()
  // When the caller passed a lockedPreset (the new type-picker flow),
  // seed from that and disable the dropdown — the director already
  // committed to a format on the picker step. Fallback to the first
  // preset for callers that don't lock anything (defensive).
  const [presetKey, setPresetKey] = useState(lockedPreset || FORMAT_PRESETS[0].key)
  const [tournamentDays, setTournamentDays] = useState(1)
  // Bracket days defaults to tournament days; user can lower it independently.
  const [bracketDaysOverride, setBracketDaysOverride] = useState(null)
  const bracketDays = bracketDaysOverride == null ? tournamentDays : bracketDaysOverride
  // Scramble-only: how many players are on each team and how many teams
  // share a starting hole at shotgun. The player-count → default-teams-per-
  // hole mapping is computed; admin can override down (never up).
  const [scrambleTeamSize, setScrambleTeamSize] = useState(4)
  const [scramblePlayersPerHoleOverride, setScramblePlayersPerHoleOverride] = useState(null)
  // Scramble variant. Stored on info.scrambleStyle so the score app and
  // admin tabs can branch on it later (e.g. Florida sit-out tracker).
  // Default 'standard' — most clubs run a standard scramble.
  const [scrambleStyle, setScrambleStyle] = useState('standard')
  // Shotgun is the common case; sequential-from-hole-1 is rare and is what
  // a daily-tee-time tournament looks like.
  const [startType, setStartType] = useState('shotgun')
  // Handicap setup. Default to 'none' so existing flows are unchanged
  // for directors who don't run handicapped events. Slope / tee name
  // / allowance only matter when mode != 'none' — they're persisted
  // either way so re-opening the modal restores the last values.
  const [handicapMode, setHandicapMode] = useState('none')
  // Course / tee data populated by the CourseSearchPicker once a course
  // is selected. All fields stay null until the director picks a tee.
  const [courseSel, setCourseSel] = useState({
    courseName: '',
    courseId: null,
    coursePar: null,
    slopeRating: null,
    courseRating: null,
    teeName: null,
    teeBoxes: [],
  })
  const [handicapAllowance, setHandicapAllowance] = useState(DEFAULT_ALLOWANCE)
  // Team Cup scoring system. Saved straight onto info.teamCupConfig so
  // the Team Cup tab reads it on first render — director never has to
  // visit the tab to set this once the tournament is created.
  const [teamCupScoring, setTeamCupScoring] = useState('official')
  const preset = FORMAT_PRESETS.find((p) => p.key === presetKey) || FORMAT_PRESETS[0]
  const isScramble = presetKey === 'scramble'
  const isTeamCup = presetKey === 'teamcup'
  const isPbcc4Ball = presetKey === 'pbcc4ball'

  // PBCC 4-Ball pre-fill: signature name, locked course, 3-day default
  // bracket. Only seeds empty fields so a director re-opening the modal
  // after editing doesn't have their typing clobbered. Date is left
  // blank intentionally — the director picks the actual tournament day.
  useEffect(() => {
    if (!isPbcc4Ball) return
    setName((n) => n || 'PBCC 4-Ball Championship')
    setCourse('Pine Bluff Country Club') // always — input is locked below
    setTournamentDays((d) => (Number(d) === 1 ? 3 : d))
    setBracketDaysOverride((b) => (b == null ? 3 : b))
  }, [isPbcc4Ball])
  const defaultPerHole = scrambleDefaultPerHole(scrambleTeamSize)
  const scramblePlayersPerHole = scramblePlayersPerHoleOverride ?? defaultPerHole

  const submit = (e) => {
    e.preventDefault()
    if (!name.trim()) return
    const payload = {
      name: name.trim(),
      date,
      course: course.trim(),
      tournamentDays: Number(tournamentDays),
      bracketDays: Number(bracketDays),
      startType,
      teeStyle: startType === 'shotgun' ? 'Shotgun start' : 'Tee times',
      // Handicap settings — applied to every player when handicapMode
      // != 'none'. The mode drives field visibility on the registration
      // form; the slope + allowance drive the per-player course
      // handicap / playing handicap calc.
      handicapMode,
      // Course / tee data persisted only when handicap mode is on; the
      // CourseSearchPicker enforces that slope + rating get set together.
      slopeRating: handicapMode === 'none' ? null : (Number(courseSel.slopeRating) || null),
      courseRating: handicapMode === 'none' ? null : (Number(courseSel.courseRating) || null),
      coursePar: handicapMode === 'none' ? null : (Number(courseSel.coursePar) || null),
      courseName: handicapMode === 'none' ? null : (courseSel.courseName || null),
      courseId: handicapMode === 'none' ? null : (courseSel.courseId || null),
      teeName: handicapMode === 'none' ? null : (courseSel.teeName || null),
      handicapAllowance: handicapMode === 'none' ? null : Number(handicapAllowance),
      ...preset.info,
    }
    if (isScramble) {
      payload.scrambleTeamSize = Number(scrambleTeamSize)
      payload.scramblePlayersPerHole = Number(scramblePlayersPerHole)
      payload.scrambleStyle = scrambleStyle
      // Smart defaults per spec: 4-man handicaps off, 2-man handicaps on,
      // 1- and 3-man default off (admin can flip on the Overview tab).
      // Ambrose is handicap-by-rule, so flip on regardless of team size.
      payload.scrambleHandicapsEnabled =
        scrambleStyle === 'ambrose' || Number(scrambleTeamSize) === 2
      payload.scrambleMulligans = 0
      // Texas scramble has a built-in min-drive rule — turn it on by
      // default so the phone scorecard's drive tracker shows up.
      payload.scrambleMinDriveEnabled = scrambleStyle === 'texas'
      payload.scrambleMinDrivesPerPlayer = scrambleStyle === 'texas' ? 4 : 0
    }
    if (isTeamCup) {
      // Persist the Team Cup config directly on info so the Team Cup tab
      // picks it up on first render — director never needs to set
      // scoringSystem manually after creation. The other config fields
      // (scats / cth / handicaps) keep their library defaults; they're
      // editable on the Team Cup tab post-creation.
      payload.teamCupConfig = {
        scoringSystem: teamCupScoring,
        scatsEnabled: false,
        scatsPerHole: 0,
        cthEnabled: false,
        cthPerPar3: 0,
        handicapsOn: false,
      }
    }
    onSave(payload, { qualifyingEventId: importEventId || null })
  }
  return (
    <Modal onClose={onClose} title="New Tournament">
      <form onSubmit={submit} className="space-y-4">
        {/* Optional qualifying-event import. None = create empty
            tournament (default existing behavior). Selecting an event
            triggers the post-create roster modal which copies the
            event's approved teams into /registrations and offers
            Set Up with AI / Manually buttons. */}
        <Field label="Import From Qualifying Event (Optional)">
          <select
            className="input"
            value={importEventId}
            onChange={(e) => setImportEventId(e.target.value)}>
            <option value="">None — start with an empty tournament</option>
            {qualifyingOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.approved} approved
              </option>
            ))}
          </select>
          {importEventId ? (
            <p className="text-sm text-gray-600 mt-1.5 leading-snug">
              Approved teams from this qualifying event will be copied into the new tournament's registrations after create.
            </p>
          ) : null}
        </Field>
        <Field label="Name">
          <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Date">
          <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field label="Course">
          <input
            className="input"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            readOnly={isPbcc4Ball}
            disabled={isPbcc4Ball}
            title={isPbcc4Ball ? 'PBCC 4-Ball is locked to Pine Bluff Country Club.' : undefined}
            style={isPbcc4Ball ? { background: 'rgba(18, 61, 38,0.06)', cursor: 'not-allowed' } : undefined}
          />
          {isPbcc4Ball ? (
            <p className="text-sm text-gray-500 mt-1">
              🔒 Locked — PBCC 4-Ball Championship is always played at Pine Bluff Country Club.
            </p>
          ) : null}
        </Field>
        <Field label="Tournament Format">
          {lockedPreset ? (
            // Locked banner — the format was chosen on the type-picker
            // step and can't be changed without going back. Visually
            // distinct from a regular input so the director sees the
            // commitment clearly.
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 border-[#123D26] bg-[#FFFFFF]">
              {preset.Icon ? <preset.Icon size={20} color="#123D26" strokeWidth={1.8} /> : null}
              <div className="flex-1">
                <div className="text-sm font-extrabold text-[#123D26] leading-tight">{preset.label}</div>
                <div className="text-sm text-gray-600 leading-snug">{preset.desc}</div>
              </div>
              <span className="flex items-center gap-1 text-sm font-bold uppercase tracking-widest text-gray-500">
                <LockIcon size={11} strokeWidth={2.5} />
                Locked
              </span>
            </div>
          ) : (
            <>
              <select className="input" value={presetKey} onChange={(e) => setPresetKey(e.target.value)}>
                {FORMAT_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
              <p className="text-sm text-gray-600 mt-1.5 leading-snug">{preset.desc}</p>
            </>
          )}
        </Field>

        <Field label="Start Type">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="startType"
                checked={startType === 'shotgun'}
                onChange={() => setStartType('shotgun')}
              />
              <span className="text-sm font-bold">Shotgun Start</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="startType"
                checked={startType === 'sequential'}
                onChange={() => setStartType('sequential')}
              />
              <span className="text-sm font-bold">Sequential from Hole 1</span>
            </label>
          </div>
          <p className="text-sm text-gray-600 mt-1.5 leading-snug">
            {startType === 'shotgun'
              ? 'Multiple groups tee off simultaneously across the course. Two waves per day are common — admin sets up the rotation in the Tee Times tab.'
              : 'Groups tee off in order from hole 1, spaced by the interval admin sets in the Tee Times tab.'}
          </p>
        </Field>
        <Field label="Handicap Mode">
          <select
            className="input"
            value={handicapMode}
            onChange={(e) => setHandicapMode(e.target.value)}>
            {HANDICAP_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="text-sm text-gray-600 mt-1.5 leading-snug">
            {(HANDICAP_MODE_OPTIONS.find((o) => o.value === handicapMode) || {}).desc}
          </p>
        </Field>
        {handicapMode !== 'none' ? (
          <>
            <SavedCoursesQuickPick onPick={setCourseSel} />
            <CourseSearchPicker value={courseSel} onChange={setCourseSel} />
            <Field label="Handicap Allowance">
              <select
                className="input"
                value={handicapAllowance}
                onChange={(e) => setHandicapAllowance(Number(e.target.value))}>
                {ALLOWANCE_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p}%</option>
                ))}
              </select>
              <p className="text-sm text-gray-600 mt-1.5 leading-snug">
                Applied to every player's course handicap. Default 100% = no
                adjustment. Lower allowance = fewer strokes = tighter field.
              </p>
            </Field>
          </>
        ) : null}

        {isTeamCup ? (
          // Team Cup ONLY — two large prominent radio tiles for the
          // scoring system. The director sets this BEFORE saving so
          // the Team Cup tab opens already-configured. Mirrors the
          // ScoringSystemCard styling in src/admin/TeamCupTab.jsx so
          // the choice feels identical in both places.
          <Field label="Team Cup — Scoring System">
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {
                  id: 'official',
                  title: 'Official Team Cup Scoring',
                  hint: '1 point per hole won, ½ point each for a halved hole. Max 18 points per match.',
                },
                {
                  id: 'custom',
                  title: 'Custom Scoring',
                  hint: '1 point per hole + 1 bonus point for winning the front nine + 1 bonus point for winning the back nine. Max 20 points per match.',
                },
              ].map((opt) => {
                const active = teamCupScoring === opt.id
                return (
                  <label
                    key={opt.id}
                    className={`relative cursor-pointer rounded-xl p-4 transition shadow-sm ${
                      active
                        ? 'bg-green-50 ring-2 ring-green-700 border border-green-700'
                        : 'bg-white border-2 border-gray-200 hover:border-green-700 hover:bg-green-50/40'
                    }`}>
                    <input
                      type="radio"
                      name="teamCupScoring"
                      value={opt.id}
                      checked={active}
                      onChange={() => setTeamCupScoring(opt.id)}
                      className="sr-only"
                      aria-label={opt.title}
                    />
                    {active ? (
                      <span
                        className="absolute top-2.5 right-2.5 flex items-center justify-center rounded-full bg-green-700 text-white"
                        style={{ width: 24, height: 24 }}
                        aria-hidden="true">
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
                          <path d="M5 10.5l3.2 3.2L15 6.8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    ) : null}
                    <div className="flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex items-center justify-center rounded-full border-2 ${
                          active ? 'border-green-700' : 'border-gray-400'
                        }`}
                        style={{ width: 22, height: 22 }}
                        aria-hidden="true">
                        {active ? (
                          <div className="rounded-full bg-green-700" style={{ width: 12, height: 12 }} />
                        ) : null}
                      </div>
                      <div className="flex-1 pr-6">
                        <div className={`font-extrabold leading-tight ${active ? 'text-green-900' : 'text-gray-900'}`} style={{ fontSize: 15 }}>
                          {opt.title}
                        </div>
                        <div className="text-sm text-gray-700 mt-1 leading-snug">
                          {opt.hint}
                        </div>
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
            <p className="text-sm text-gray-600 mt-2 leading-snug">
              You can change this later on the Team Cup tab.
            </p>
          </Field>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tournament Days">
            <select
              className="input"
              value={tournamentDays}
              onChange={(e) => setTournamentDays(Number(e.target.value))}>
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          {isScramble ? (
            <Field label="Team Size (players)">
              <select
                className="input"
                value={scrambleTeamSize}
                onChange={(e) => {
                  setScrambleTeamSize(Number(e.target.value))
                  setScramblePlayersPerHoleOverride(null)
                }}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="Bracket Days">
              <select
                className="input"
                value={bracketDays}
                onChange={(e) => setBracketDaysOverride(Number(e.target.value))}>
                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
          )}
        </div>

        {isScramble ? (
          <Field label="Scramble Style">
            <select
              className="input"
              value={scrambleStyle}
              onChange={(e) => setScrambleStyle(e.target.value)}>
              <option value="standard">Standard (most common)</option>
              <option value="texas">Texas — minimum drive per player</option>
              <option value="florida">Florida — chosen-shot player sits out next</option>
              <option value="shamble">Shamble — best drive then individual play</option>
              <option value="ambrose">Ambrose — team handicap stroke deduction</option>
              <option value="vegas">Las Vegas — die roll picks the drive</option>
            </select>
            <p className="text-sm text-gray-600 mt-1.5 leading-snug">
              {scrambleStyle === 'standard'  && 'All four players hit; team picks the best shot and plays from there until holed out.'}
              {scrambleStyle === 'texas'     && 'Same as standard but every player must contribute a minimum number of drives. Min-drive tracker turns on automatically (default 4 per player).'}
              {scrambleStyle === 'florida'   && 'Whoever\'s shot the team selects sits out the next stroke — adds strategy without changing the scoring math.'}
              {scrambleStyle === 'shamble'   && 'All hit drives, the team picks the best one, then each player plays their own ball into the hole. Best individual score counts.'}
              {scrambleStyle === 'ambrose'   && 'Standard scramble with a team handicap deduction. Handicaps toggle on by default; admin enters per-player handicaps in Flights & Teams.'}
              {scrambleStyle === 'vegas'     && 'A die roll on each tee determines whose drive the team plays from. Equal-opportunity variant.'}
            </p>
          </Field>
        ) : null}
        {isScramble ? (
          <Field label="Teams per hole (shotgun grouping)">
            <select
              className="input"
              value={scramblePlayersPerHole}
              onChange={(e) => setScramblePlayersPerHoleOverride(Number(e.target.value))}>
              {scramblePerHoleOptions(scrambleTeamSize).map((n) => (
                <option key={n} value={n}>
                  {n} team{n === 1 ? '' : 's'} per hole
                  {n === defaultPerHole ? ' (default)' : ''}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-600 mt-1.5 leading-snug">
              {explainPerHole(scrambleTeamSize, scramblePlayersPerHole)}
            </p>
          </Field>
        ) : null}
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary">Create</button>
        </div>
      </form>
    </Modal>
  )
}

// Quick-pick chip strip rendered above the CourseSearchPicker in the
// tournament create + edit flows. Reads /club/courses live; clicking a
// chip pre-populates the picker via its onChange prop. The picker is
// untouched — this is purely additive.
function SavedCoursesQuickPick({ onPick }) {
  const [courses, setCourses] = useState({})
  useEffect(() => watchClubCourses(setCourses), [])
  const list = Object.entries(courses)
    .map(([id, v]) => ({ id, ...(v || {}) }))
    .sort((a, b) => (a.courseName || '').localeCompare(b.courseName || ''))
  if (list.length === 0) return null
  return (
    <div className="mb-2">
      <p className="text-sm uppercase tracking-widest font-bold text-gray-600 mb-1.5">
        Quick-pick from Handicap & Courses
      </p>
      <div className="flex flex-wrap gap-1.5">
        {list.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(quickPickPayload(c))}
            className="px-2.5 py-1 rounded-full text-sm font-bold bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300">
            {c.courseName}
          </button>
        ))}
      </div>
    </div>
  )
}

// Translate a saved /club/courses record into the picker's value shape.
// New shape carries a `teeBoxes` array; legacy single-tee records get
// synthesized into a 1-element array so the picker can still drive its
// tee-selection dropdown. Slope / rating / teeName are left null when
// multiple tees exist so the director is forced to pick the correct
// tee for this specific tournament.
function quickPickPayload(c) {
  const teeBoxes = Array.isArray(c?.teeBoxes) && c.teeBoxes.length > 0
    ? c.teeBoxes.map((t) => ({
        name: t.name || '',
        rating: Number(t.rating) || 0,
        slope: Number(t.slope) || 0,
        par: Number(t.par) || Number(c.coursePar) || 72,
      }))
    : (c?.teeName && Number(c?.slopeRating) > 0 && Number(c?.courseRating) > 0
        ? [{
            name: c.teeName,
            slope: Number(c.slopeRating),
            rating: Number(c.courseRating),
            par: Number(c.coursePar) || 72,
          }]
        : [])
  // Auto-pick when there's only one tee so the director doesn't have
  // to drill back into the dropdown for trivial cases.
  if (teeBoxes.length === 1) {
    const t = teeBoxes[0]
    return {
      courseName: c.courseName || '',
      courseId: c.courseId || null,
      coursePar: Number(c.coursePar) || Number(t.par) || null,
      slopeRating: t.slope,
      courseRating: t.rating,
      teeName: t.name,
      teeBoxes,
    }
  }
  return {
    courseName: c.courseName || '',
    courseId: c.courseId || null,
    coursePar: Number(c.coursePar) || null,
    slopeRating: null,
    courseRating: null,
    teeName: null,
    teeBoxes,
  }
}

export function Modal({ title, onClose, children, size = 'md' }) {
  // `size` controls the max-width:
  //   md (default) → max-w-md (existing forms)
  //   lg           → max-w-2xl (type picker — needs room for 2-col tile grid)
  //   xl           → max-w-4xl (inline Registration / Players surfaces)
  const widthClass = size === 'xl' ? 'max-w-4xl' : size === 'lg' ? 'max-w-2xl' : 'max-w-md'
  // NOTE: rendered via a PORTAL to document.body. The admin dashboard's
  // RollingSections animates its children with `transform: translateY(...)`, and
  // a `position: fixed` element inside a TRANSFORMED ancestor is positioned
  // relative to that ancestor's box (not the viewport) — which trapped/clipped
  // this modal's top so it couldn't be scrolled into view. Portaling to <body>
  // escapes the transform so `fixed` is viewport-relative again. The panel is
  // capped at the viewport height with a fixed header + internally-scrolling
  // body, so the header/title is ALWAYS visible and the top is never clipped.
  const modal = (
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto"
      style={{ backgroundColor: 'rgba(18, 61, 38,0.78)' }}
      onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`rounded-2xl ${widthClass} w-full flex flex-col`}
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(18, 61, 38, 0.30)',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
            maxHeight: 'calc(100vh - 2rem)',
          }}
          onClick={(e) => e.stopPropagation()}>
          {/* Header — pinned, never scrolls out of view */}
          <div className="flex items-start justify-between px-5 pt-5 pb-4 shrink-0">
            <div>
              <span className="pbcc-gold-rule" aria-hidden="true" />
              <h3 className="pbcc-h2 text-xl mt-1">{title}</h3>
            </div>
            <button
              className="text-2xl leading-none -mt-1"
              style={{ color: '#123D26' }}
              onClick={onClose}
              aria-label="Close">
              ×
            </button>
          </div>
          {/* Body — scrolls internally */}
          <div className="px-5 pb-5 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  )
}
